import { useState } from 'react'
import Grid from '@material-ui/core/Grid'
import IconButton from '@material-ui/core/IconButton'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import ExpandLessIcon from '@material-ui/icons/ExpandLess'
import Collapse from '@material-ui/core/Collapse'
import useEffectAsync from '../../../hooks/useEffectAsync'
import { client } from '../../../client'
import AuthenticationService from '../../../services/AuthenticationService'
import PageLoadingIndicator from '../../../components/PageLoadingIndicator/PageLoadingIndicator'
import RichTextEditor from '../../../components/RichTextEditor/RichTextEditor'
import { useTranslation } from 'react-i18next'

const EditTemplatesSection = () => {
  const { t } = useTranslation('settingsPage')
  const userId = AuthenticationService.getUserId()

  const [state, setState] = useState({ text: '', practiceOptions: [], templateTypeOptions: [], collapsed: {} })

  // Initialize
  useEffectAsync(async () => {
    const [practiceOptionsRaw, templateTypeOptions] = await Promise.all([
      client.getPracticeOptions(true),
      client.getTemplateTypeOptions()
    ])

    const allowedNames = ['Slaapmakend', 'Mulder & Van Schie']
    const practiceOptions = (practiceOptionsRaw || [])
      .filter((p) => allowedNames.includes(p.name))
      .sort((a, b) => allowedNames.indexOf(a.name) - allowedNames.indexOf(b.name))

    // initialize collapsed state for each practice+field to true (collapsed)
    const fields = ['acquaintanceNote', 'intakeNote', 'treatmentPlan']
    const collapsed = {}
    for (const practice of practiceOptions) {
      for (const field of fields) {
        collapsed[`${practice.id}_${field}`] = true
      }
    }

    setState((s) => ({ ...s, practiceOptions, templateTypeOptions, collapsed }))
  }, [])

  const loadTemplate = async (practiceId, fieldName) => {
    const response = await client.getTemplate(userId, practiceId, fieldName)
    if (response?.status === 'success' && response?.data?.template?.text) {
      return response.data.template.text
    }
    return ''
  }
  // Preload all combinations so the editors show current content
  useEffectAsync(async () => {
    if (!state.practiceOptions?.length || !state.templateTypeOptions?.length) return
    const fields = ['acquaintanceNote', 'intakeNote', 'treatmentPlan']
    const updates = {}
    for (const practice of state.practiceOptions) {
      for (const field of fields) {
        // eslint-disable-next-line no-await-in-loop
        updates[`${practice.id}_${field}`] = await loadTemplate(practice.id, field)
      }
    }
    setState((s) => ({ ...s, ...updates }))
  }, [state.practiceOptions, state.templateTypeOptions])

  const handleChange = async (practiceId, fieldName, text) => {
    setState((s) => ({ ...s, [`${practiceId}_${fieldName}`]: text }))
    await client.createTemplate(text, userId, practiceId, fieldName)
  }

  if (!state?.practiceOptions?.length || !state?.templateTypeOptions?.length) {
    return <PageLoadingIndicator />
  }

  const toggleCollapsed = (key) =>
    setState((s) => ({ ...s, collapsed: { ...s.collapsed, [key]: !s.collapsed[key] } }))

  return (
    <form autoComplete="off">
      <Grid container spacing={4} direction="column">
        {state.practiceOptions.map((practice) => (
          <Grid item key={practice.id}>
            {[ 'acquaintanceNote', 'intakeNote', 'treatmentPlan' ].map((fieldName) => {
              const key = `${practice.id}_${fieldName}`
              const title = `${t('editTemplatePractice')}: ${practice.name} — ${t('template')}: ${fieldName === 'acquaintanceNote' ? 'Kennismaking' : fieldName === 'intakeNote' ? 'Intake' : 'Behandelplan'}`
              const ExpandedIcon = state.collapsed[key] ? ExpandMoreIcon : ExpandLessIcon
              return (
                <div key={key} style={{ marginBottom: 24, border: '1px solid #eee', borderRadius: 6 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px' }}>
                    <div
                      style={{ fontWeight: 600, cursor: 'pointer' }}
                      role="button"
                      tabIndex={0}
                      onClick={() => toggleCollapsed(key)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault()
                          toggleCollapsed(key)
                        }
                      }}
                    >
                      {title}
                    </div>
                    <IconButton aria-label={state.collapsed[key] ? 'Expand' : 'Collapse'} onClick={() => toggleCollapsed(key)}>
                      <ExpandedIcon fontSize="small" />
                    </IconButton>
                  </div>
                  <Collapse in={!state.collapsed[key]} timeout="auto" unmountOnExit>
                    <div style={{ padding: 12 }}>
                      <RichTextEditor
                        initial={''}
                        value={state[key]}
                        onChange={(v) => handleChange(practice.id, fieldName, v)}
                      />
                    </div>
                  </Collapse>
                </div>
              )
            })}
          </Grid>
        ))}
      </Grid>
    </form>
  )
}

export default EditTemplatesSection
